import { Arrayable } from '@/types/general'

class Generator {
    private settings = {
        chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
        digits: '0123456789',
        specials: '!@#$%^&*()[]+=-_<>.,?|',
        useChars: true,
        useDigits: true,
        useSpecials: true,
        length: 16,
        minChars: 3,
        minDigits: 3,
        minSpecials: 3
    }

    /**
     * Use the browser's crypto API to generate
     * a cryptographically random number between
     * 0 and max.
     *
     * @param {number} max max value
     * @returns {number} random number between [0, max]
     */
    private randomNumber(max: number): number {
        const digits = new Uint8Array(2)
        window.crypto.getRandomValues(digits)
        return digits[0] % max
    }

    /**
     * Randomly select a character from a string or a string
     * from an array
     *
     * @param {Arrayable<string>} set
     * @returns {string} character selected
     */
    private pick(set: Arrayable<string>): string {
        return set[this.randomNumber(set.length)]
    }

    /**
     * Check if the password matches the requirements
     * defined in the settings.
     *
     * @param {string} password
     * @returns {boolean} valid
     */
    private valid(password: string): boolean {
        // invalid if not matching determined length
        let valid = password.length === this.settings.length
        if (valid && this.settings.useChars) {
            // match for all letters
            const chars = password.match(/[A-Za-z]/g)
            // if there are matches and the required amount
            // of matches, valid stays true
            valid = valid && chars != null && chars.length >= this.settings.minChars
        }
        if (valid && this.settings.useDigits) {
            // match for all digits
            const digits = password.match(/\d/g)
            valid = valid && digits != null && digits.length >= this.settings.minDigits
        }
        if (valid && this.settings.useSpecials) {
            // match for special characters
            const specials = password.match(/[!@#$%^&*()[\]+=\-_<>.,?|]/g)
            valid = valid && specials != null && specials.length >= this.settings.minSpecials
        }
        return valid
    }

    /**
     * Generate a password thatmatches the requirements
     * defined in the settings.
     *
     * @returns {string} generated password
     */
    public generate(): string {
        // determine which sets are available to use
        const sets = []
        if (this.settings.useChars) sets.push(this.settings.chars)
        if (this.settings.useDigits) sets.push(this.settings.digits)
        if (this.settings.useSpecials) sets.push(this.settings.specials)
        const password = []
        // keep count of tries so we can break
        // and endless loop caused by bad config
        // (i.e. minDigits > length, etc)
        let tries = 0
        while (!this.valid(password.join(''))) {
            tries += 1
            if (tries > 5000) {
                throw new Error('Password generation took too long, change password settings')
            }
            // pick a set: chars, digits, specials
            const set = this.pick(sets)
            // pick a character and add it to the password
            password.push(this.pick(set))
            // if password already at the required length
            // (but not valid), remove the first element
            // so we can continue generating
            if (password.length > this.settings.length) {
                password.shift()
            }
        }
        return password.join('')
    }
}

export default new Generator()
